import { useState, useMemo, useEffect, useRef } from "react";
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';

import { getProjectMaterials, updateProjectMaterial, deleteProjectMaterial, MaterialTrackerItem } from "../../api/materialTracker";

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaTrash,
  FaBoxes,
  FaHistory
} from 'react-icons/fa';

import { getTableHeaderStyle, getFormSelectContainerStyle, getFormSelectStyle, getFormLabelStyle, DesignSystem } from '../../styles/designSystem';
import { MaterialForm } from './MaterialForm';
import { MaterialLifecycleModal } from './MaterialLifecycleModal';

interface ProjectMaterialTrackerProps {
  projectId: string;
}

// Material row component
const MaterialRow = ({ 
  material, 
  onUpdate, 
  onDelete,
  isNew = false
}: { 
  material: MaterialTrackerItem, 
  onUpdate: (material: MaterialTrackerItem) => Promise<void>,
  onDelete: (materialId: string | undefined) => void,
  isNew?: boolean
}) => {
  const [form, setForm] = useState({ ...material });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm({ ...material });
  }, [material]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ordered':
        return DesignSystem.colors.status.inProgress;
      case 'delivered':
        return DesignSystem.colors.status.completed;
      case 'pending':
        return DesignSystem.colors.status.notStarted;
      case 'delayed':
        return DesignSystem.colors.risk.high;
      default:
        return DesignSystem.colors.primary.textMuted;
    }
  };

  const saveChanges = async (updatedForm: MaterialTrackerItem) => {
    if (!material.id) return;
    
    setIsSaving(true);
    try {
      await onUpdate(updatedForm);
      setForm(updatedForm);
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  return (
    <div className={`group hover:bg-[#fafafa]/50 transition-all duration-200 ${isNew ? 'bg-[#f5f5f5]/30' : ''}`}>
      {/* Main Row */}
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: '0.5px solid #eaeaea' }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-[#fafafa] transition-colors relative z-10"
        >
          <FaChevronDown className={`w-3 h-3 text-[#aaa] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

        {/* Status Indicator */}
        <div className="mr-4 flex items-center relative z-10">
          <div className="relative">
            {isSaving && (
              <div className="absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin border-t-blue-500/60 border-r-blue-500/60" />
            )}
            <FaCircle 
              className="w-2 h-2" 
              style={{ color: getStatusColor(form.status || 'pending') }}
            />
          </div>
        </div>

        {/* Material Name */}
        <div className="flex-1 min-w-0 relative z-10">
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => handleFieldBlur('name', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 placeholder-[#aaa] font-medium text-[#171717] text-base"
            placeholder="Material name..."
            style={{
              fontSize: '15px',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: 360,
            }}
          />
          {form.id && (
            <div className="text-xs text-[#888] mt-1 truncate">
              ID: {form.id}
            </div>
          )}
        </div>

        {/* Tracking Number */}
        <div className="mx-6 w-32 relative z-10">
          <input
            type="text"
            value={form.tracking_number || ''}
            onChange={(e) => setForm(prev => ({ ...prev, tracking_number: e.target.value }))}
            onBlur={(e) => handleFieldBlur('tracking_number', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 text-sm text-[#444]"
            placeholder="Tracking #..."
          />
        </div>

        {/* Quantity */}
        <div className="mx-6 w-20 relative z-10">
          <input
            type="number"
            value={form.quantity || ''}
            onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 0)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 text-sm text-[#444] text-center"
            placeholder="0"
          />
        </div>

        {/* Status */}
        <div className="mx-6 w-24 relative z-10">
          <select
            value={form.status || 'pending'}
            onChange={(e) => handleFieldChange('status', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 text-sm appearance-none cursor-pointer"
            style={{
              color: getStatusColor(form.status || 'pending'),
              fontWeight: 500,
            }}
          >
            <option value="pending">Pending</option>
            <option value="ordered">Ordered</option>
            <option value="delivered">Delivered</option>
            <option value="delayed">Delayed</option>
          </select>
        </div>

        {/* Ordered At */}
        <div className="mx-6 w-24 flex items-center text-xs text-[#555] relative z-10">
          <input
            type="date"
            value={form.ordered_at ? form.ordered_at.split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldChange('ordered_at', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="bg-transparent border-0 focus:outline-none text-xs w-full"
          />
        </div>

        {/* ETA */}
        <div className="ml-6 w-24 flex items-center text-xs text-[#555] relative z-10">
          <input
            type="date"
            value={form.eta ? form.eta.split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldChange('eta', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="bg-transparent border-0 focus:outline-none text-xs w-full"
          />
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Material Details */}
            <div className="lg:col-span-2 space-y-4">
              {/* Description */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Description</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('description', e.target.value)}
                  className="w-full text-sm text-[#444] bg-white border border-[#eaeaea] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-0 focus:border-[#888] resize-none"
                  placeholder="Material description, specifications, or notes..."
                  rows={6}
                  style={{ 
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: DesignSystem.typography.body.fontSize 
                  }}
                />
              </div>
            </div>

            {/* Material Info and Actions */}
            <div className="space-y-4">
              
              {/* Project Material ID */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Project Material ID</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="text"
                    value={form.id || 'Not assigned'}
                    readOnly
                    className="w-full bg-[#fafafa] border-0 focus:outline-none text-sm text-[#555] cursor-not-allowed"
                    placeholder="Generated automatically"
                  />
                </div>
                <p className="text-xs text-[#888] mt-1">Used for warranty tracking and material identification</p>
              </div>
              
              {/* Item/SKU */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Item/SKU</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="text"
                    value={form.item || ''}
                    onChange={(e) => setForm(prev => ({ ...prev, item: e.target.value }))}
                    onBlur={(e) => handleFieldBlur('item', e.target.value)}
                    className="w-full bg-transparent border-0 focus:outline-none"
                    placeholder="Item number or SKU"
                  />
                </div>
              </div>

              {/* Ship Date (ESD) */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Ship Date (ESD)</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="date"
                    value={form.esd ? form.esd.split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      handleFieldChange('esd', value);
                    }}
                    className="w-full bg-transparent border-0 focus:outline-none text-xs"
                  />
                </div>
              </div>

              {/* Install Date (ISD) */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Install Date (ISD)</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="date"
                    value={form.isd ? form.isd.split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      handleFieldChange('isd', value);
                    }}
                    className="w-full bg-transparent border-0 focus:outline-none text-xs"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="pt-4">
                <label className="block mb-2" style={getFormLabelStyle()}>Actions</label>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('openMaterialLifecycle', { detail: { materialId: material.id, material } }))}
                    className="flex items-center space-x-2 px-3 py-2 text-sm text-[#171717] hover:bg-[#f5f5f5] rounded-md transition-colors"
                  >
                    <FaHistory className="w-3 h-3" />
                    <span>View Lifecycle</span>
                  </button>
                <button
                  onClick={() => material.id && onDelete(material.id)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Material</span>
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ProjectMaterialTracker = ({ projectId }: ProjectMaterialTrackerProps) => {
  const { keycloak } = useKeycloak();
  const [materials, setMaterials] = useState<MaterialTrackerItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialTrackerItem | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>('');

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Material lifecycle modal handler
  useEffect(() => {
    const handleOpenLifecycle = (event: CustomEvent) => {
      const { materialId, material } = event.detail;
      setSelectedMaterialId(materialId);
      setSelectedMaterial(material);
      setShowLifecycleModal(true);
    };

    window.addEventListener('openMaterialLifecycle', handleOpenLifecycle as EventListener);
    return () => window.removeEventListener('openMaterialLifecycle', handleOpenLifecycle as EventListener);
  }, []);

  const loadMaterials = async () => {
    if (!keycloak.token || !projectId) return;

    try {
      const response = await getProjectMaterials(keycloak.token, projectId);
      setMaterials(response.results || []);
    } catch (error) {
      console.error('Error loading materials:', error);
      toast.error('Failed to load materials');
    }
  };

  // Guard against React StrictMode's dev-only double-invoke firing this load
  // twice on mount, while still reloading on a genuine projectId/token
  // change (this component does not remount when switching projects while
  // already on this tab).
  const lastLoadedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${keycloak.token}|${projectId}`;
    if (lastLoadedKeyRef.current === key) return;
    lastLoadedKeyRef.current = key;
    loadMaterials();
  }, [keycloak.token, projectId]);

  // Filter materials
  const filteredMaterials = useMemo(() => {
    if (!materials) return [];
    
    return materials.filter((material) => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        if (
          !material.name?.toLowerCase().includes(searchLower) &&
          !material.item?.toLowerCase().includes(searchLower) &&
          !material.tracking_number?.toLowerCase().includes(searchLower) &&
          !material.description?.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      // Status filter
      if (statusFilter !== 'all' && material.status !== statusFilter) {
        return false;
    }
      
      return true;
    });
  }, [materials, searchQuery, statusFilter]);

  const updateMaterialHandler = async (material: MaterialTrackerItem) => {
    if (!keycloak.token || !material.id) return;

    try {
      await updateProjectMaterial(keycloak.token, projectId, material.id, material);
      await loadMaterials();
    } catch (error) {
      console.error('Error updating material:', error);
      throw error;
    }
  };

  const deleteMaterialHandler = async (materialId: string | undefined) => {
    if (!keycloak.token || !materialId) return;
    
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    
    try {
      await deleteProjectMaterial(keycloak.token, projectId, materialId);
      toast.success('Material deleted successfully');
      await loadMaterials();
    } catch (error: any) {
      console.error('Error deleting material:', error);
      toast.error(error.message || 'Failed to delete material');
    }
  };

  const getStatusCount = (status: string) => {
    return materials?.filter(material => material.status === status).length || 0;
  };

  return (
    <div className="min-h-screen">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-shadow duration-200 ${isScrolled ? 'shadow-[0_4px_12px_rgba(0,0,0,0.06)]' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #eaeaea' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Stats */}
                <div className="flex items-center space-x-6">
                  {/* Search */}
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#aaa] w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search materials..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-[#fafafa] border-0 rounded-lg focus:outline-none focus:ring-0 focus:bg-white transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-[#888]">
                      {filteredMaterials.length} of {materials.length} materials
                    </span>
                    <div className="w-px h-4 bg-[#ddd]"></div>
                    <span className="text-[#888]">
                      {getStatusCount('delivered')} delivered
            </span>
                  </div>
          </div>
          
                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
              <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-[#f5f5f5] text-[#171717] border border-[#eaeaea]' 
                        : 'bg-[#fafafa] text-[#555] hover:bg-[#fafafa] border border-transparent'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {statusFilter !== 'all' && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-[#171717] text-white rounded-full">
                        1
                      </span>
                    )}
              </button>
            
                  {/* Add Material */}
                  <button
                    onClick={() => setShowMaterialForm(true)}
                    className="flex items-center space-x-2 bg-[#171717] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#333] transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                  >
                    <FaPlus className="w-3.5 h-3.5" />
                    <span>Add Material</span>
                  </button>
          </div>
        </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-[#f5f5f5]">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="ordered">Ordered</option>
                        <option value="delivered">Delivered</option>
                        <option value="delayed">Delayed</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Active Filters & Clear */}
                  {statusFilter !== 'all' && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-[#888]">Active filters:</span>
                        {statusFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-[#f5f5f5] text-[#171717] rounded-md">
                            Status: {statusFilter}
                            <button onClick={() => setStatusFilter('all')} className="ml-1 text-[#888] hover:text-[#171717]">×</button>
                          </span>
                        )}
                      </div>
                          <button
                        onClick={() => setStatusFilter('all')}
                        className="text-xs text-[#888] hover:text-[#444] font-medium"
                          >
                        Clear all
                          </button>
                        </div>
              )}
                </div>
          )}
            </div>
            
            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="w-4 mr-4"></div> {/* Status indicator space */}
                <div className="flex-1" role="columnheader">Material</div>
                <div className="mx-6 w-32" role="columnheader">Tracking #</div>
                <div className="mx-6 w-20" role="columnheader">Qty</div>
                <div className="mx-6 w-24" role="columnheader">Status</div>
                <div className="mx-6 w-24" role="columnheader">Ordered</div>
                <div className="ml-6 w-24" role="columnheader">ETA</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Material List - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
          {/* Material Rows */}
          <div>
            {filteredMaterials.map((material) => (
              <MaterialRow 
                key={material.id} 
                material={material} 
                onUpdate={updateMaterialHandler}
                onDelete={deleteMaterialHandler}
              />
            ))}

          </div>
        </div>

        {/* Empty State */}
        {filteredMaterials.length === 0 && (
          <div className="text-center py-16">
            <div className="text-[#aaa] mb-4">
              <FaBoxes className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-light text-[#171717] mb-2">No materials found</h3>
            <p className="text-[#555] font-light mb-8">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Get started by adding your first material.'
              }
            </p>
            <button
              onClick={() => setShowMaterialForm(true)}
              className="bg-white border border-[#eaeaea] text-[#555] px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#fafafa] hover:border-[#eaeaea] transition-colors"
            >
              Add First Material
            </button>
          </div>
        )}
    </div>

      {/* Material Form Modal */}
      {showMaterialForm && (
        <MaterialForm
          projectId={projectId}
          isOpen={showMaterialForm}
          onClose={() => setShowMaterialForm(false)}
          onSuccess={loadMaterials}
        />
      )}

      {/* Material Lifecycle Modal */}
      {showLifecycleModal && selectedMaterialId && (
        <MaterialLifecycleModal
          projectId={projectId}
          materialId={selectedMaterialId}
          material={selectedMaterial || undefined}
          isOpen={showLifecycleModal}
          onClose={() => {
            setShowLifecycleModal(false);
            setSelectedMaterial(null);
            setSelectedMaterialId('');
          }}
        />
      )}
    </div>
  );
}; 